import { confirmedCommunication } from './confirmedCommunication';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Row,
  Space,
  Spin,
  Tabs,
  Typography,
  Upload,
} from 'antd';
import { CopyOutlined, ReloadOutlined, SaveOutlined, UploadOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import adminAxios from './adminAxios';
import './AdminContentEditors.css';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const API_BASE = resolveFrontendApiBase();

interface GiftEmailConfig {
  brandName?: string;
  senderName?: string;
  senderEmail?: string;
  supportEmail?: string;
  dashboardUrl?: string;
  primaryColor?: string;
  textMain?: string;
  textMuted?: string;
  cardBackground?: string;
  bodyBackground?: string;
  borderColor?: string;
  logoImage?: string;
  mascotImage?: string;
}

interface GiftEmailPreviewContent {
  subject: string;
  html: string;
  text: string;
}

interface GiftEmailPreview {
  buyer: GiftEmailPreviewContent;
  recipient: GiftEmailPreviewContent;
}

interface GiftEmailAdminResponse {
  config: GiftEmailConfig;
  preview: GiftEmailPreview;
}

interface GiftEmailSample {
  giftCode?: string;
  credits?: number;
  planName?: string;
  buyerEmail?: string;
  senderName?: string;
  recipientEmail?: string;
  giftMessage?: string;
}

const DEFAULT_SAMPLE: GiftEmailSample = {
  giftCode: 'GIFT-LOVE-1234',
  credits: 500,
  planName: 'Forever credits',
  buyerEmail: 'giver@example.com',
  recipientEmail: 'reader@example.com',
  giftMessage: 'Thanks for being the best beta reader! Hope you enjoy these credits.',
};

const GiftEmailAdmin: React.FC = () => {
  const [configForm] = Form.useForm<GiftEmailConfig>();
  const [sampleForm] = Form.useForm<GiftEmailSample>();
  const [loadedConfig, setLoadedConfig] = useState<GiftEmailConfig | null>(null);
  const [preview, setPreview] = useState<GiftEmailPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [sendingTest, setSendingTest] = useState<boolean>(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setInlineError(null);
    try {
      const { data } = await adminAxios.get<GiftEmailAdminResponse>(`${API_BASE}/admin/gift-emails/config`);
      setLoadedConfig(data.config);
      configForm.setFieldsValue(data.config);
      sampleForm.setFieldsValue(DEFAULT_SAMPLE);
      setPreview(data.preview);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to load gift email configuration.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [configForm, sampleForm]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaveConfig = async (values: GiftEmailConfig) => {
    setSaving(true);
    setInlineError(null);
    try {
      await adminAxios.post(`${API_BASE}/admin/gift-emails/config`, values);
      message.success('Gift email configuration updated.');
      await loadConfig();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to update gift email configuration.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    const configValues = configForm.getFieldsValue();
    setPreviewLoading(true);
    setInlineError(null);
    try {
      const { data } = await adminAxios.post<{ preview: GiftEmailPreview }>(
        `${API_BASE}/admin/gift-emails/preview`,
        {
          sample: sampleValues,
          configOverrides: configValues,
        }
      );
      setPreview(data.preview);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to refresh preview.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    const sampleValues = sampleForm.getFieldsValue();
    if (!sampleValues.buyerEmail && !sampleValues.recipientEmail) {
      message.warning('Enter at least one email address in the sample inputs to send a test.');
      return;
    }
    setSendingTest(true);
    setInlineError(null);
    try {
      await confirmedCommunication(`${API_BASE}/admin/gift-emails/test-send`, {
        sample: sampleValues,
        configOverrides: configForm.getFieldsValue(),
        sendBuyer: Boolean(sampleValues.buyerEmail),
        sendRecipient: Boolean(sampleValues.recipientEmail),
      });
      message.success('Test email sent.');
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const msg = detail || 'Failed to send test email.';
      setInlineError(msg);
      message.error(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      message.success(`${label} copied to clipboard.`);
    } catch {
      message.error('Unable to copy to clipboard.');
    }
  };

  const convertFileToBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handleImageUpload = async (file: File, field: 'logoImage' | 'mascotImage') => {
    try {
      const base64 = await convertFileToBase64(file);
      configForm.setFieldsValue({ [field]: base64 });
      message.success('Image uploaded.');
    } catch {
      message.error('Failed to upload image.');
    }
    return false;
  };

  const logoValue = Form.useWatch('logoImage', configForm);
  const mascotValue = Form.useWatch('mascotImage', configForm);

  const previewTabs = useMemo(() => {
    if (!preview) {
      return <Empty description="Preview not available yet." />;
    }
    const panels = [
      { key: 'buyer', label: 'Buyer Email', content: preview.buyer },
      { key: 'recipient', label: 'Recipient Email', content: preview.recipient },
    ];
    return (
      <Tabs defaultActiveKey="buyer">
        {panels.map((panel) => (
          <TabPane tab={panel.label} key={panel.key}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Card size="small" bordered={false} className="admin-content-editor-preview-panel">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                      Subject
                    </Text>
                    <Input
                      readOnly
                      value={panel.content.subject}
                      className="admin-content-editor-readonly-input"
                    />
                  </div>
                </Space>
                <Space style={{ marginTop: 16 }}>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={() => copyToClipboard(panel.content.subject, 'Subject')}
                  >
                    Copy Subject
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    size="small"
                    onClick={() => copyToClipboard(panel.content.text, 'Plain text body')}
                  >
                    Copy Text Body
                  </Button>
                </Space>
              </Card>
              <Card size="small" bordered={false} bodyStyle={{ padding: 0 }} className="admin-content-editor-preview-panel">
                <iframe
                  title={`${panel.key}-gift-email-preview`}
                  srcDoc={panel.content.html}
                  className="admin-content-editor-preview-frame"
                />
              </Card>
              <Card size="small" title="Plain Text">
                <pre style={{ whiteSpace: 'pre-wrap' }}>{panel.content.text}</pre>
              </Card>
            </Space>
          </TabPane>
        ))}
      </Tabs>
    );
  }, [preview]);

  return (
    <Spin spinning={loading && !loadedConfig}>
      <div className="admin-content-editor">
        <section className="admin-content-editor-header" aria-labelledby="gift-email-admin-title">
          <div>
            <div className="admin-content-editor-kicker">Email editor</div>
            <h2 id="gift-email-admin-title">Gift Email Templates</h2>
            <p>Configure buyer and recipient gift email branding, sample data, and preview output.</p>
          </div>
        </section>
        {inlineError && (
          <Alert
            className="admin-content-editor-inline-alert"
            type="error"
            showIcon
            message="Gift email editor needs attention"
            description={inlineError}
          />
        )}
      <Row gutter={24} className="admin-content-editor-layout">
        <Col span={10}>
          <Card title="Gift Email Branding & Sender" className="admin-content-editor-card admin-content-editor-card--editor">
            <Form
              layout="vertical"
              form={configForm}
              initialValues={loadedConfig || {}}
              onFinish={handleSaveConfig}
            >
              <Form.Item label="Brand Name" name="brandName">
                <Input placeholder="Brand name shown in emails" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Sender Name" name="senderName">
                    <Input placeholder="e.g. ScribeShadow Team" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Sender Email" name="senderEmail">
                    <Input placeholder="support@example.com" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Support Email" name="supportEmail">
                    <Input placeholder="support@example.com" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Dashboard URL" name="dashboardUrl">
                    <Input placeholder="https://app.scribeshadow.com/account/gifts" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Primary Color" name="primaryColor">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Border Color" name="borderColor">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Text Main" name="textMain">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Text Muted" name="textMuted">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Card Background" name="cardBackground">
                    <Input type="color" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Body Background" name="bodyBackground">
                    <Input type="color" />
                  </Form.Item>
                </Col>
              </Row>
              <div className="admin-content-editor-swatches" aria-label="Token color reference">
                <span style={{ background: 'var(--text-info)' }} />
                <span style={{ background: 'var(--surface-base)' }} />
                <span style={{ background: 'var(--border-subtle)' }} />
                <span style={{ background: 'var(--text-main)' }} />
                <Text type="secondary">Use the preview to compare raw email colors against app token references.</Text>
              </div>
              <Divider />
              <Form.Item
                label="Logo Image (URL or upload)"
                name="logoImage"
                extra="Shown centered at the top. Ideal size ~140px wide."
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input placeholder="Paste an image URL or use upload" />
                  <Space>
                    <Upload
                      beforeUpload={(file) => handleImageUpload(file, 'logoImage')}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    {logoValue && (
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => configForm.setFieldsValue({ logoImage: '' })}
                      >
                        Remove
                      </Button>
                    )}
                  </Space>
                  {logoValue && (
                    <Image
                      src={logoValue}
                      alt="Logo preview"
                      style={{ marginTop: 8, maxWidth: 160 }}
                      fallback=""
                    />
                  )}
                </Space>
              </Form.Item>
              <Form.Item
                label="Mascot Image (URL or upload)"
                name="mascotImage"
                extra="Appears below the hero text in both emails. Portrait rectangle recommended."
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input placeholder="Paste an image URL or use upload" />
                  <Space>
                    <Upload
                      beforeUpload={(file) => handleImageUpload(file, 'mascotImage')}
                      showUploadList={false}
                      accept="image/*"
                    >
                      <Button icon={<UploadOutlined />}>Upload Image</Button>
                    </Upload>
                    {mascotValue && (
                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => configForm.setFieldsValue({ mascotImage: '' })}
                      >
                        Remove
                      </Button>
                    )}
                  </Space>
                  {mascotValue && (
                    <Image
                      src={mascotValue}
                      alt="Mascot preview"
                      style={{ marginTop: 8, maxWidth: 220 }}
                      fallback=""
                    />
                  )}
                </Space>
              </Form.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginTop: 8 }}>
                <Button
                  icon={<SaveOutlined />}
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                >
                  Save Config
                </Button>
                <Button
                  onClick={() => loadedConfig && configForm.setFieldsValue(loadedConfig)}
                  disabled={!loadedConfig || saving}
                >
                  Reset to Saved
                </Button>
              </Space>
            </Form>
          </Card>

          <Card title="Sample Inputs" className="admin-content-editor-card admin-content-editor-card--stacked admin-content-editor-card--actions">
            <Form layout="vertical" form={sampleForm} initialValues={DEFAULT_SAMPLE}>
              <Form.Item label="Gift Code" name="giftCode">
                <Input placeholder="GIFT-LOVE-1234" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item label="Credits" name="credits">
                    <InputNumber min={1} max={100000} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Plan Name" name="planName">
                    <Input placeholder="Forever credits" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Buyer Email" name="buyerEmail">
                <Input type="email" placeholder="giver@example.com" />
              </Form.Item>
              <Form.Item label="Sender Name (optional)" name="senderName">
                <Input placeholder="Shown to recipient if provided" />
              </Form.Item>
              <Form.Item label="Recipient Email" name="recipientEmail">
                <Input type="email" placeholder="reader@example.com" />
              </Form.Item>
              <Form.Item label="Gift Message" name="giftMessage">
                <Input.TextArea rows={3} placeholder="Optional note" />
              </Form.Item>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={refreshPreview}
                  block
                  loading={previewLoading}
                >
                  Refresh Preview
                </Button>
                <Button
                  icon={<MailOutlined />}
                  type="primary"
                  onClick={handleSendTestEmail}
                  block
                  loading={sendingTest}
                >
                  Send Test Email
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
        <Col span={14}>
          <Card
            className="admin-content-editor-card admin-content-editor-card--preview"
            title="Email Preview"
            extra={
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshPreview}
                loading={previewLoading}
              >
                Refresh
              </Button>
            }
          >
            <Spin spinning={previewLoading && !preview}>
              {previewTabs}
            </Spin>
          </Card>
        </Col>
      </Row>
      </div>
    </Spin>
  );
};

export default GiftEmailAdmin;
